import {Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, shareReplay} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

export interface ChangelogEntry {
  tag: string;
  name: string;
  published_at: string;
  html_url: string;
  headline: string;
  highlights: string[];
}

export interface Changelog {
  environment: string;
  entries: ChangelogEntry[];
}

/** Records when the user last saw the changelog, so a new release can pop it up again. */
const SEEN_COOKIE = 'cp-whats-new-seen';
const SEEN_COOKIE_MAX_AGE = 60 * 60 * 24 * 730; // two years

@Injectable({providedIn: 'root'})
export class WhatsNewService {
  readonly entries = signal<ChangelogEntry[] | null>(null);
  /** True while the newest release is newer than the last time the user saw the changelog. */
  readonly hasUnseen = signal(false);

  private readonly changelog$: Observable<ChangelogEntry[]>;

  constructor(private http: HttpClient) {
    // The changelog is rewritten on every deploy and decides whether the popover
    // opens, so a cached copy would hide fresh release notes. CloudFront serves
    // /assets/changelog.json with `Cache-Control: no-store` (see portal_stack.py);
    // the cache buster covers every other host (local ng serve, direct S3).
    this.changelog$ = this.http.get<Changelog>(`/assets/changelog.json?t=${Date.now()}`).pipe(
      map(log => log?.entries ?? []),
      catchError(() => of<ChangelogEntry[]>([])),
      tap(entries => {
        this.entries.set(entries);
        this.hasUnseen.set(this.isUnseen(entries));
      }),
      shareReplay(1)
    );
  }

  /** Loads the changelog once per session; replays to later subscribers. */
  load(): Observable<ChangelogEntry[]> {
    return this.changelog$;
  }

  /** Remembers that the changelog was displayed now, so it won't pop up again. */
  markSeen(): void {
    document.cookie =
      `${SEEN_COOKIE}=${encodeURIComponent(new Date().toISOString())}` +
      `; path=/; max-age=${SEEN_COOKIE_MAX_AGE}; SameSite=Lax`;
    this.hasUnseen.set(false);
  }

  private isUnseen(entries: ChangelogEntry[]): boolean {
    const latest = this.latestRelease(entries);
    if (latest === null) {
      return false;
    }
    const lastSeen = this.lastSeen();
    // Never seen it: show it once, so the entry point is discovered.
    return lastSeen === null || latest > lastSeen;
  }

  private latestRelease(entries: ChangelogEntry[]): number | null {
    const timestamps = entries
      .map(entry => Date.parse(entry.published_at))
      .filter(ts => !isNaN(ts));
    return timestamps.length > 0 ? Math.max(...timestamps) : null;
  }

  private lastSeen(): number | null {
    const cookie = document.cookie
      .split('; ')
      .find(c => c.startsWith(`${SEEN_COOKIE}=`));
    if (!cookie) {
      return null;
    }
    const seen = Date.parse(decodeURIComponent(cookie.substring(SEEN_COOKIE.length + 1)));
    return isNaN(seen) ? null : seen;
  }
}